import { useEffect, useMemo, useRef, useState } from 'react';
import { View } from 'native-base';
import { isEmpty } from 'lodash';

import { useGetAttachments } from '@app/features/store-reports/hooks/get-attachments.hook';
import { StoreReportViewer, StoreReportViewerSource } from '@app/features/store-reports/components/store-report-viewer';
import { ResourceLoader } from '@app/components/shared';
import { GetReportDataForSiteQueryParams } from '@pimm/services/lib/store-inspections';
import { useGetReportDataForSite } from '@app/features/store-reports/hooks/get-report-data-for-site.hook';
import { useGetReportComposer } from '@app/features/store-reports/hooks/get-report-composer.hook';
import { useAppLocale } from '@pimm/common';
import { Platform } from 'react-native';

type StoreInspectionPreviewProps = Partial<GetReportDataForSiteQueryParams> & {};

export const StoreInspectionPreview = ({ date, siteId, storeReportId, ..._props }: StoreInspectionPreviewProps) => {
  const webviewRef = useRef<any>(null); // keep any to fix type issue between different webview/iframe
  const { locale } = useAppLocale();
  const [jsonTemplate, setJsonTemplate] = useState<any>();
  const [source, setSource] = useState<StoreReportViewerSource & { isLoading?: boolean }>();

  const queryReportData = useGetReportDataForSite({ date, siteId, storeReportId });
  const report = useGetReportComposer(queryReportData.data);
  const queryAttachments = useGetAttachments(report?.attachmentsIds);

  const isWeb = Platform.OS === 'web';
  const reportTemplate = queryReportData.data?.reportData?.reportData?.reportTemplate;
  const templateUrl = reportTemplate?.file?.fileUrl;
  const pdfComposerUrl = reportTemplate?.pdfComposer?.file?.fileUrl;

  const handleLoad = () => {
    if (isWeb) {
      if (!webviewRef.current?.src || !source?.uri) return;
      if (!webviewRef.current.src.includes(source.uri)) return;
    }

    setSource(_prev => ({ ..._prev, isLoading: false }));
  };

  // This effect is responsible for fetching report json template
  useEffect(() => {
    if (!templateUrl) return;
    // TODO: need to revisit and use current hook useReportTemplate
    const populateTemplate = async (_url: string) => {
      const encodedUrl = isWeb && __DEV__ ? `https://corsproxy.io/?${encodeURIComponent(_url)}` : _url;
      const template = await fetch(encodedUrl).then(async _ => {
        if (!_.ok) return undefined;
        const result = await _.json();
        return result;
      });
      setJsonTemplate(template);
    };

    populateTemplate(templateUrl);
  }, [templateUrl]);

  // This effect is responsible for updating web source
  useEffect(() => {
    const cleanUrl = pdfComposerUrl?.replace(/\s/g, '')?.replace(/(\.[^\/?#]+)?([?#].*)?$/, '/index.html');
    setSource({ isLoading: true, uri: cleanUrl });
  }, [pdfComposerUrl]);

  useEffect(() => {
    if (!source || source?.isLoading) return;
    if (!webviewRef.current || (isWeb && !webviewRef.current.src)) return;
    if (!jsonTemplate || !report?.reportData) return;
    if (!isEmpty(report.attachmentsIds) && queryAttachments.isPending) return;

    // TODO: need to revisit if needed to support different platform
    if (webviewRef.current) {
      report.attachments = queryAttachments.data;

      try {
        const message = {
          type: 'compose-web',
          detail: {
            ...jsonTemplate,
            data: report.composeReport(locale),
          },
        };

        if (isWeb) {
          const url = new URL(webviewRef.current.src);
          webviewRef.current.contentWindow.postMessage(message, url.origin);
          return;
        }

        const jsCode = `
          setTimeout(() => {
            dispatchEvent(new CustomEvent('compose', { detail: ${JSON.stringify(message.detail)} }));
          }, 200);
        `;
        // @ts-ignore
        webviewRef?.current?.injectJavaScript(jsCode);
      } catch (error) {
        console.error('Could not send data to iframe', error);
      }
    }
  }, [locale, jsonTemplate, queryAttachments.isPending, report?.reportData, source?.isLoading]);

  return (
    <View w="full" h="full">
      <ResourceLoader
        flex={1}
        isLoading={(!isEmpty(report?.attachmentsIds) && queryAttachments.isPending) || queryReportData.isLoading}
        isEmpty={isEmpty(queryReportData?.data)}
      >
        <StoreReportViewer ref={webviewRef} source={source} onLoad={handleLoad} />
      </ResourceLoader>
    </View>
  );
};
